import { useState } from "react";
import { ChatCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

// Admin-only card that mints a signed Request Radar link (triage mode) for a WO.
// SILENT — no SMS/email is sent. Admin copies the URL and shares it manually
// so tenants can self-diagnose with Claude AI before we roll a truck.
export default function TriageLinkCard({ job, onMinted }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const mintedAt = job?.triage_link_minted_at;

  const buildFull = (u) => {
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    return `${window.location.origin}${u.startsWith("/") ? "" : "/"}${u}`;
  };

  const mintAndCopy = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${job.id}/triage-link`);
      const full = buildFull(r.data?.url);
      setLink(full);
      try {
        await navigator.clipboard.writeText(full);
        toast.success("Diagnostic chat link copied to clipboard");
      } catch {
        toast.success("Diagnostic chat link ready — tap Copy");
      }
      if (onMinted) onMinted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not mint link");
    } finally {
      setBusy(false);
    }
  };

  const copyAgain = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="mt-4 rugged-card p-3 bg-white border-l-4 border-indigo-600" data-testid="triage-link-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-black uppercase tracking-wider text-indigo-800 flex items-center gap-1.5">
            <ChatCircle weight="fill" size={13}/> Send Diagnostic Chat (AI Triage)
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
            Generate a private link the tenant can open on their phone to self-diagnose the issue with our AI.
            Often prevents a truck roll. <span className="font-bold">Silent — no SMS/email is sent.</span> Copy &amp; share manually.
          </div>
          {mintedAt && !link && (
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">
              Last minted: {new Date(mintedAt).toLocaleString()}
            </div>
          )}
        </div>
        <button
          data-testid="mint-triage-link-btn"
          onClick={mintAndCopy}
          disabled={busy}
          className="shrink-0 tactile-btn h-10 px-3 font-black uppercase tracking-wider text-xs border-2 border-indigo-700 bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1.5 disabled:opacity-60">
          <Sparkle weight="fill" size={13}/>
          {busy ? "…" : (mintedAt ? "Re-mint & Copy" : "Get Link & Copy")}
        </button>
      </div>
      {link && (
        <div className="mt-2 flex items-center gap-2">
          <input
            data-testid="triage-link-input"
            readOnly
            value={link}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 h-9 px-2 text-xs font-mono border-2 border-slate-300 bg-slate-50"/>
          <button
            data-testid="triage-link-copy-btn"
            onClick={copyAgain}
            className="shrink-0 h-9 px-3 font-black uppercase text-[11px] tracking-wider border-2 border-slate-800 bg-white hover:bg-slate-100">
            Copy
          </button>
          <a
            data-testid="triage-link-open-btn"
            href={link} target="_blank" rel="noreferrer"
            className="shrink-0 h-9 px-3 font-black uppercase text-[11px] tracking-wider border-2 border-indigo-700 bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center">
            Open
          </a>
        </div>
      )}
    </div>
  );
}
